import React from "react";
import { Trophy, TrendingUp, Activity, ArrowRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Profile() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const platformFeatures = [{
    id: 1,
    text: "Digital coverage requests"
  }, {
    id: 2,
    text: "Streamlined scheduling coordination"
  }, {
    id: 3,
    text: "Electronic confirmations"
  }, {
    id: 4,
    text: "Secure communication"
  }, {
    id: 5,
    text: "Mobile-friendly experience"
  }, {
    id: 6,
    text: "App-based access (where available)"
  }];
  return <section data-section-id="46" id="digitalplatform" ref={sectionRef} className="relative min-h-[800px] w-full overflow-hidden bg-white py-20 lg:py-32 flex items-center">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-20%] right-[-10%] h-[800px] w-[800px] rounded-full bg-[hsl(213,68%,95%)] opacity-60 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-20%] left-[-10%] h-[600px] w-[600px] rounded-full bg-[hsl(47,65%,95%)] opacity-50 blur-[100px]" />
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613743/img/noise_texture.png)] opacity-5 mix-blend-overlay" />
      </div>

      <div className="container relative z-10 mx-auto px-6">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:items-center">
          
          {}
          <div className={`flex flex-col gap-8 transition-all duration-1000 ease-out ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}`}>
            <div className="space-y-4">
              <h2 className="text-6xl font-extrabold tracking-tight text-[hsl(215,68%,14%)] sm:text-7xl lg:text-8xl">
                BUILT ON A MODERN <span className="text-[hsl(213,68%,59%)]">PLATFORM.</span>
              </h2>
              <p className="max-w-2xl text-xl text-muted-foreground sm:text-2xl leading-relaxed">
                Empowering surgical teams with high-speed matching, automated credentialing, and seamless real-time coordination across every facility.
              </p>
            </div>

            <div className="flex flex-col gap-6 sm:flex-row sm:items-end">
              <div className="flex flex-col gap-2">
                <span className="text-[14px] font-bold uppercase tracking-wider text-[hsl(47,65%,48%)]">Facilities</span>
                <Button asChild size="lg" className="h-14 px-8 text-lg font-bold bg-gradient-to-r from-[#1E4FA3] to-[#0A2E6E] text-white hover:opacity-90 transition-opacity shadow-lg shadow-primary/20">
                  <Link to="/facilities/">
                    Request Coverage
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[14px] font-bold uppercase tracking-wider text-[hsl(47,65%,48%)]">Clinicians</span>
                <Button asChild variant="outline" size="lg" className="h-14 px-8 text-lg border-[hsl(215,68%,14%)]/20 bg-white text-[hsl(215,68%,14%)] hover:bg-[hsl(215,68%,95%)] backdrop-blur-sm">
                  <Link to="/clinicians/" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border rounded-md has-[>svg]:px-4 h-14 px-8 text-lg border-[hsl(215,68%,14%)]/20 bg-white text-[hsl(215,68%,14%)] backdrop-blur-sm inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border rounded-md has-[>svg]:px-4 h-14 px-8 text-lg border-[hsl(215,68%,14%)]/20 bg-white text-[hsl(215,68%,14%)] backdrop-blur-sm !border-none border-1 !shadow-[0px_4px_8px_rgba(20,21,31,0.18),_0px_2px_4px_rgba(20,21,31,0.13)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-[hsl(215,68%,95%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-[hsl(215,68%,95%)]">
                    Join Our Network
                  </Link>
                </Button>
              </div>
            </div>

            <ul className="grid gap-y-4 gap-x-8 sm:grid-cols-2">
              {platformFeatures.map((feature, i) => <li key={feature.id} data-index={i} className="flex items-center gap-3 text-lg text-muted-foreground">
                  <Check className="h-5 w-5 text-[hsl(213,68%,59%)] shrink-0" />
                  <span>{feature.text}</span>
                </li>)}
            </ul>
            
          </div>

          {}
          <div className="relative flex justify-center perspective-1000 transition-all duration-1000 delay-300 ease-out translate-y-0 opacity-100 !border-none !border-transparent">
            {}
            <div className="absolute top-1/2 left-1/2 -z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-b from-[hsl(213,68%,59%)]/10 to-transparent blur-3xl" />

            <div className="group relative w-full max-w-md transform transition-transform duration-500 hover:scale-[1.02] hover:rotate-0">
              <div className="aspect-square overflow-hidden rounded-xl border border-border bg-white shadow-2xl transition-all duration-300 group-hover:shadow-primary/20">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613743/img/dashboard-mockup.png" alt="Dashboard mock-up" className="h-full w-full object-cover !border-none !border-transparent !text-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}